/**
 * PromptFederation · The secondary, prompt-only Ring of Twelve system.
 *
 * Public page (free engagement — no purchase code, no passkey). Renders the
 * complete manual + per-asset download cards + an interactive "Ask the Ring"
 * assembler that hands users copy-paste prompt bundles for any standard AI
 * chat platform.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";

const STAGE_OPTIONS = [
  { id: "ring_of_three", label: "Ring of Three", subtitle: "Simplest · 3 AIs + Agent Zero" },
  { id: "ring_of_six", label: "Ring of Six", subtitle: "Daily-use · 3 AIs covering 6 perspectives" },
  { id: "ring_of_twelve", label: "Ring of Twelve", subtitle: "High-stakes · full nested ring" },
];

function downloadHref(slug) {
  return `${API}/prompt-federation/download/${slug}`;
}

export default function PromptFederation() {
  const [index, setIndex] = useState(null);
  const [manual, setManual] = useState("");
  const [question, setQuestion] = useState("");
  const [stage, setStage] = useState("ring_of_three");
  const [bundle, setBundle] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copiedSlug, setCopiedSlug] = useState(null);

  useEffect(() => {
    api.promptFederationIndex().then(setIndex).catch(() => setIndex(null));
    api.promptFederationManual().then(setManual).catch(() => setManual(""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const assemble = async () => {
    if (busy) return;
    setBusy(true);
    setBundle(null);
    try {
      const data = await api.promptFederationAssemble(
        stage,
        question.trim() || null,
        null,
      );
      setBundle(data);
    } catch (e) {
      setBundle({ error: e.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text, slug) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSlug(slug);
      setTimeout(() => setCopiedSlug(null), 1600);
    } catch (e) {
      console.warn("clipboard write failed", e);
    }
  };

  const aggregator = useMemo(
    () => index?.items?.find((it) => it.slug === "prompt-1"),
    [index],
  );

  return (
    <div
      className="px-6 md:px-12 py-10 max-w-6xl mx-auto"
      data-testid="prompt-federation-page"
    >
      {/* ── Hero ────────────────────────────────────────────────────── */}
      <div className="mb-10">
        <div className="font-mono text-[10px] uppercase tracking-[0.36em] text-glow-magenta mb-2">
          Free engagement · doctrine layer
        </div>
        <h1
          className="font-display text-4xl md:text-5xl lg:text-6xl text-glow-cyan leading-[1.05]"
          data-testid="prompt-federation-title"
        >
          The Prompt Federation
        </h1>
        <p className="text-[var(--text-2)] mt-4 max-w-3xl text-base leading-relaxed">
          A nested Ring of Twelve cognitive system built entirely from prompts.
          Drop these into any standard AI chat — ChatGPT, Claude, Gemini, Grok
          — and run real multi-perspective reasoning without the Cathedral's
          internal stack. Secondary, simpler, but it works. Designed to harden
          corporate AIs against agreeableness drift and surface the truth they
          would otherwise file off.
        </p>
        {index?.cathedral_public_fingerprint && (
          <div className="font-mono text-[9px] uppercase tracking-[0.24em] text-[var(--text-3)] mt-4">
            sealed · fingerprint {index.cathedral_public_fingerprint.slice(0, 16)}
          </div>
        )}
      </div>

      {/* ── Downloads grid ──────────────────────────────────────────── */}
      <section className="mb-12" data-testid="prompt-federation-downloads">
        <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-glow-gold mb-3">
          ⌬ Download · sealed assets
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {index?.items?.map((it) => (
            <a
              key={it.slug}
              href={downloadHref(it.slug)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`download-${it.slug}`}
              className="block p-4 border border-[var(--chrome)] hover:border-[var(--cyan)] hover:bg-[rgba(0,240,255,0.03)] transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-display text-base text-glow-cyan truncate">
                    {it.title}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--text-3)] mt-1">
                    {it.filename} · {(it.size_bytes / 1024).toFixed(1)} KB
                  </div>
                  <div className="text-[var(--text-2)] text-xs mt-2 leading-relaxed">
                    {it.description}
                  </div>
                </div>
                <div className="font-display text-xl text-glow-gold shrink-0">↓</div>
              </div>
            </a>
          ))}
          {!index && (
            <div className="text-[var(--text-3)] font-mono text-xs italic col-span-2">
              Loading sealed assets…
            </div>
          )}
        </div>
      </section>

      {/* ── Ask the Ring assembler ──────────────────────────────────── */}
      <section className="mb-12" data-testid="ask-the-ring">
        <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-glow-magenta mb-3">
          ◈ Ask the Ring · generate a copy-paste bundle
        </div>
        <div className="border border-[var(--chrome)] p-5 bg-[rgba(7,8,26,0.5)]">
          <label className="block font-mono text-[9px] uppercase tracking-[0.22em] text-[var(--text-3)] mb-1.5">
            Your question (optional — leave blank to get a reusable template)
          </label>
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="e.g. Should I take this job offer?"
            data-testid="ask-the-ring-question"
            rows={2}
            className="w-full bg-[var(--void-2)] border border-[var(--chrome)] focus:border-[var(--cyan)] focus:outline-none text-[var(--text)] font-mono text-[12px] px-3 py-2"
          />
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
            {STAGE_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                onClick={() => setStage(opt.id)}
                data-testid={`stage-pick-${opt.id}`}
                className={`text-left p-3 border transition-colors ${
                  stage === opt.id
                    ? "border-[var(--cyan)] bg-[rgba(0,240,255,0.06)]"
                    : "border-[var(--chrome)] hover:border-[var(--cyan)]"
                }`}
              >
                <div
                  className={`font-display text-sm ${
                    stage === opt.id ? "text-glow-cyan" : "text-[var(--text)]"
                  }`}
                >
                  {opt.label}
                </div>
                <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--text-3)] mt-0.5">
                  {opt.subtitle}
                </div>
              </button>
            ))}
          </div>
          <button
            onClick={assemble}
            disabled={busy}
            data-testid="ask-the-ring-go"
            className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] px-5 py-2 border border-[var(--gold)] text-glow-gold hover:bg-[rgba(255,178,0,0.08)] transition-colors disabled:opacity-40"
          >
            {busy ? "Assembling…" : "Generate bundle ▸"}
          </button>
        </div>
      </section>

      {/* ── Assembled bundle ────────────────────────────────────────── */}
      {bundle && (
        <section className="mb-12" data-testid="ring-bundle">
          {bundle.error ? (
            <div className="font-mono text-glow-red text-sm">{bundle.error}</div>
          ) : (
            <>
              <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-glow-violet mb-2">
                ⌖ Your bundle · {bundle.stage.replace(/_/g, " ")}
              </div>
              <div className="text-[var(--text-2)] text-sm mb-4 leading-relaxed">
                {bundle.instructions}
              </div>
              <div className="space-y-3">
                {bundle.slots.map((s, i) => (
                  <div
                    key={`slot-${i}`}
                    className="border border-[var(--chrome)] p-4"
                    data-testid={`ring-slot-${i}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-display text-sm text-glow-cyan">
                        Slot {i + 1} · {s.perspective}
                      </div>
                      <button
                        onClick={() => copy(s.prompt, `slot-${i}`)}
                        data-testid={`ring-slot-copy-${i}`}
                        className="font-mono text-[9px] uppercase tracking-[0.22em] px-2 py-1 border border-[var(--gold)] text-glow-gold hover:bg-[rgba(255,178,0,0.08)]"
                      >
                        {copiedSlug === `slot-${i}` ? "copied ✓" : "copy"}
                      </button>
                    </div>
                    <pre className="font-mono text-[11px] text-[var(--text)] whitespace-pre-wrap leading-relaxed bg-[var(--void-2)] border border-[var(--chrome)] p-3 max-h-[180px] overflow-auto">
                      {s.prompt}
                    </pre>
                  </div>
                ))}
                {aggregator && (
                  <div className="border border-[var(--gold)] p-4 bg-[rgba(255,178,0,0.04)]">
                    <div className="font-display text-sm text-glow-gold mb-2">
                      Final step · paste this into a fresh chat, then feed it
                      every slot's response
                    </div>
                    <a
                      href={downloadHref("prompt-1")}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="ring-aggregator-download"
                      className="font-mono text-[10px] uppercase tracking-[0.22em] inline-block px-3 py-1.5 border border-[var(--gold)] text-glow-gold hover:bg-[rgba(255,178,0,0.08)]"
                    >
                      Download Agent Zero (Prompt 1) ↓
                    </a>
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      )}

      {/* ── Full manual (markdown rendered raw) ─────────────────────── */}
      <section data-testid="prompt-federation-manual">
        <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-glow-gold mb-3">
          ⌬ Manual · full text
        </div>
        <div className="border border-[var(--chrome)] p-5 md:p-7 bg-[rgba(2,3,12,0.6)]">
          {manual ? (
            <pre className="font-mono text-[12px] md:text-[13px] text-[var(--text-2)] whitespace-pre-wrap leading-relaxed">
              {manual}
            </pre>
          ) : (
            <div className="text-[var(--text-3)] font-mono text-xs italic">
              Loading the canonical manual…
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
